import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle2,
  XCircle,
  Clock,
  MinusCircle,
  RefreshCw,
  Mail,
  Send,
  ArrowLeft,
  Lock,
  LogOut,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTH_KEY = "ihm.admin.basic";

const STATUS_MAP = {
  delivered: { label: "Delivered", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", Icon: CheckCircle2 },
  failed: { label: "Failed", cls: "bg-red-500/15 text-red-300 border-red-500/30", Icon: XCircle },
  pending: { label: "Pending", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30", Icon: Clock },
  skipped: { label: "Skipped", cls: "bg-white/5 text-white/50 border-white/10", Icon: MinusCircle },
  unknown: { label: "Unknown", cls: "bg-white/5 text-white/50 border-white/10", Icon: MinusCircle },
};

function StatusBadge({ status, label, Icon: IconOverride }) {
  const meta = STATUS_MAP[status] || STATUS_MAP.unknown;
  const Icon = IconOverride || meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.18em] ${meta.cls}`}>
      <Icon size={11} />
      {label}: {meta.label}
    </span>
  );
}

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60000);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min${m === 1 ? "" : "s"} ago`;
  if (h < 24) return `${h} hr${h === 1 ? "" : "s"} ago`;
  return `${d} day${d === 1 ? "" : "s"} ago`;
}
function formatTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      setError("Enter username and password.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const basic = btoa(`${username}:${password}`);
      await axios.post(`${API}/admin/login`, null, {
        headers: { Authorization: `Basic ${basic}` },
      });
      localStorage.setItem(AUTH_KEY, basic);
      onLogin(basic);
    } catch (err) {
      if (err?.response?.status === 401) setError("Invalid credentials.");
      else setError(err?.response?.data?.detail || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-screen"
      className="min-h-screen bg-[#0e0e10] text-white flex items-center justify-center px-5"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.03] p-8 md:p-10 backdrop-blur-md"
      >
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0E0E10] border border-white/10 overflow-hidden">
            <img src="/logo.png" alt="" className="h-full w-full object-cover" />
          </span>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/50">House of IHM · Admin</p>
            <h1 className="mt-1 font-heading font-bold text-[22px] tracking-tight">Sign in</h1>
          </div>
        </div>

        <p className="mt-6 text-[13px] leading-relaxed text-white/60">
          This area is restricted. Enter your admin credentials to view contact submissions.
        </p>

        <div className="mt-8 space-y-5">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/50">Username</label>
            <input
              type="text"
              data-testid="admin-username"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="mt-2 w-full h-11 rounded-xl bg-white/[0.04] border border-white/10 px-4 text-[14px] text-white outline-none focus:border-[#2997ff] transition-colors"
              placeholder="your username"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/50">Password</label>
            <input
              type="password"
              data-testid="admin-password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full h-11 rounded-xl bg-white/[0.04] border border-white/10 px-4 text-[14px] text-white outline-none focus:border-[#2997ff] transition-colors"
              placeholder="••••••••"
            />
          </div>
        </div>

        {error && (
          <p data-testid="admin-login-error" className="mt-5 text-[12px] text-red-300">{error}</p>
        )}

        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={loading}
          className="mt-7 inline-flex items-center justify-center gap-2 w-full h-11 rounded-full bg-white text-[#0e0e10] text-[14px] font-medium hover:bg-[#2997ff] hover:text-white transition-all duration-500 disabled:opacity-60"
        >
          <Lock size={14} />
          {loading ? "Signing in…" : "Sign in"}
        </button>

        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-white/40 hover:text-white/70"
        >
          <ArrowLeft size={12} /> Back to site
        </Link>
      </form>
    </div>
  );
}

function Dashboard({ basic, onLogout }) {
  const [data, setData] = useState({ count: 0, submissions: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [tick, setTick] = useState(0);

  const fetchLog = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/contact/log?limit=20`, {
        headers: { Authorization: `Basic ${basic}` },
      });
      setData(res.data);
      setRefreshedAt(new Date());
      setError(null);
    } catch (e) {
      if (e?.response?.status === 401) {
        onLogout();
        return;
      }
      setError(e?.response?.data?.detail || "Could not load submissions.");
    } finally {
      setLoading(false);
    }
  }, [basic, onLogout]);

  useEffect(() => {
    fetchLog();
    const id = setInterval(fetchLog, 30000);
    return () => clearInterval(id);
  }, [fetchLog]);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const lastEnquiry = data.submissions?.[0];
  const lastAgo = useMemo(
    () => (lastEnquiry ? timeAgo(lastEnquiry.timestamp) : "—"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastEnquiry, tick]
  );

  return (
    <div data-testid="page-admin-log" className="min-h-screen bg-[#0e0e10] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 md:py-12">
        {/* Top bar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="admin-back-link"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 backdrop-blur-md text-white/80 hover:bg-white/10 transition-colors"
              aria-label="Back to site"
            >
              <ArrowLeft size={16} />
            </Link>
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/50">Admin · Log</p>
              <h1 className="mt-1 font-heading font-bold text-2xl md:text-3xl tracking-tight">Contact submissions</h1>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <span
              data-testid="last-enquiry-badge"
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-md px-4 py-2 text-[11px] font-mono uppercase tracking-[0.2em] text-white/80"
            >
              <span className={`h-1.5 w-1.5 rounded-full ${lastEnquiry ? "bg-[#2997ff] animate-pulse" : "bg-white/30"}`} />
              {lastEnquiry ? `Last enquiry · ${lastAgo}` : "No enquiries yet"}
            </span>
            <button
              type="button"
              data-testid="admin-refresh"
              onClick={fetchLog}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-md px-4 py-2 text-[11px] font-mono uppercase tracking-[0.2em] text-white/80 hover:bg-white/10 disabled:opacity-50"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
              {loading ? "Refreshing" : "Refresh"}
            </button>
            <button
              type="button"
              data-testid="admin-logout"
              onClick={onLogout}
              className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-4 py-2 text-[11px] font-mono uppercase tracking-[0.2em] text-red-300 hover:bg-red-500/20"
            >
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>

        {/* Meta strip */}
        <div className="mt-6 flex flex-wrap gap-3 text-[11px] font-mono uppercase tracking-[0.22em] text-white/50">
          <span>{data.count} record{data.count === 1 ? "" : "s"}</span>
          <span>·</span>
          <span>Refreshed {refreshedAt ? refreshedAt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"}</span>
          <span>·</span>
          <span>Auto-refreshes every 30s</span>
        </div>

        {error && (
          <div data-testid="admin-error" className="mt-8 rounded-2xl border border-red-500/30 bg-red-500/10 p-5 text-[14px] text-red-200">
            {error}
          </div>
        )}

        {/* Desktop: table */}
        <div className="mt-10 hidden md:block">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/50 bg-white/[0.04]">
                  <th className="px-5 py-4 font-medium">Name</th>
                  <th className="px-5 py-4 font-medium">Email</th>
                  <th className="px-5 py-4 font-medium">Message</th>
                  <th className="px-5 py-4 font-medium whitespace-nowrap">Time</th>
                  <th className="px-5 py-4 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {!loading && data.submissions.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-16 text-center text-white/50 text-[14px]">
                      No submissions yet.
                    </td>
                  </tr>
                )}
                {data.submissions.map((s, i) => (
                  <tr
                    key={s.id}
                    data-testid={`admin-row-${i}`}
                    className={`border-t border-white/[0.06] transition-colors ${s.handled ? "bg-emerald-500/[0.04] hover:bg-emerald-500/[0.07]" : "hover:bg-white/[0.04]"}`}
                  >
                    <td className="px-5 py-5 align-top">
                      <div className="flex items-start gap-2.5">
                        {s.handled && <CheckCircle2 size={16} className="text-emerald-400 shrink-0 mt-1" data-testid={`handled-tick-${i}`} />}
                        <div>
                          <div className="font-heading font-semibold text-[15px] text-white">{s.name}</div>
                          {s.priority_tag && (
                            <span className="mt-1 inline-flex items-center rounded-full border border-[#2997ff]/30 bg-[#2997ff]/10 px-2 py-0.5 text-[9px] font-mono uppercase tracking-[0.18em] text-[#2997ff]">
                              {s.priority_tag}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-5 align-top">
                      <a href={`mailto:${s.email}`} className="text-[13px] text-[#2997ff] hover:underline break-all">{s.email}</a>
                    </td>
                    <td className="px-5 py-5 align-top max-w-[420px]">
                      <p className="text-[13px] leading-relaxed text-white/80 line-clamp-3">{s.message}</p>
                    </td>
                    <td className="px-5 py-5 align-top whitespace-nowrap text-[12px] text-white/60 font-mono">
                      <div>{formatTime(s.timestamp)}</div>
                      <div className="text-white/40 mt-1">{timeAgo(s.timestamp)}</div>
                    </td>
                    <td className="px-5 py-5 align-top">
                      <div className="flex flex-col gap-1.5">
                        {s.handled && (
                          <span data-testid={`handled-badge-${i}`} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/15 px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300">
                            <CheckCircle2 size={11} /> Handled
                          </span>
                        )}
                        <StatusBadge status={s.notification_status} label="Email" Icon={Mail} />
                        <StatusBadge status={s.autoreply_status} label="Reply" Icon={Mail} />
                        <StatusBadge status={s.telegram_status} label="Tele" Icon={Send} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile: stacked cards */}
        <div className="mt-8 grid grid-cols-1 gap-4 md:hidden">
          {!loading && data.submissions.length === 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-center text-white/50 text-[14px]">
              No submissions yet.
            </div>
          )}
          {data.submissions.map((s, i) => (
            <article
              key={s.id}
              data-testid={`admin-card-${i}`}
              className={`rounded-2xl border p-5 ${s.handled ? "border-emerald-500/30 bg-emerald-500/[0.05]" : "border-white/10 bg-white/[0.03]"}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    {s.handled && <CheckCircle2 size={15} className="text-emerald-400 shrink-0" />}
                    <h3 className="font-heading font-semibold text-[16px] text-white">{s.name}</h3>
                  </div>
                  {s.priority_tag && (
                    <span className="mt-2 inline-flex items-center rounded-full border border-[#2997ff]/30 bg-[#2997ff]/10 px-2 py-0.5 text-[9px] font-mono uppercase tracking-[0.18em] text-[#2997ff]">
                      {s.priority_tag}
                    </span>
                  )}
                  <a href={`mailto:${s.email}`} className="mt-1 block text-[12px] text-[#2997ff] hover:underline break-all">{s.email}</a>
                </div>
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/40 whitespace-nowrap">{timeAgo(s.timestamp)}</span>
              </div>
              <p className="mt-3 text-[13px] leading-relaxed text-white/75 line-clamp-4">{s.message}</p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {s.handled && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/15 px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300">
                    <CheckCircle2 size={11} /> Handled
                  </span>
                )}
                <StatusBadge status={s.notification_status} label="Email" Icon={Mail} />
                <StatusBadge status={s.autoreply_status} label="Reply" Icon={Mail} />
                <StatusBadge status={s.telegram_status} label="Tele" Icon={Send} />
              </div>
            </article>
          ))}
        </div>

        <p className="mt-12 text-[10px] font-mono uppercase tracking-[0.22em] text-white/30">
          House of IHM · Admin · Where Vision Meets Execution
        </p>
      </div>
    </div>
  );
}

export default function AdminLog() {
  const [basic, setBasic] = useState(() => localStorage.getItem(AUTH_KEY));
  const [verifying, setVerifying] = useState(Boolean(basic));

  // Verify cached credentials on mount — clears session if the server rejects them
  useEffect(() => {
    if (!basic) {
      setVerifying(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await axios.post(`${API}/admin/login`, null, {
          headers: { Authorization: `Basic ${basic}` },
        });
        if (!cancelled) setVerifying(false);
      } catch (_) {
        if (!cancelled) {
          localStorage.removeItem(AUTH_KEY);
          setBasic(null);
          setVerifying(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [basic]);

  const handleLogin = (newBasic) => setBasic(newBasic);
  const handleLogout = () => {
    localStorage.removeItem(AUTH_KEY);
    setBasic(null);
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-[#0e0e10] text-white flex items-center justify-center">
        <div className="inline-flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.22em] text-white/60">
          <RefreshCw size={14} className="animate-spin" /> Verifying session…
        </div>
      </div>
    );
  }

  if (!basic) return <LoginScreen onLogin={handleLogin} />;
  return <Dashboard basic={basic} onLogout={handleLogout} />;
}
